import React from 'react';
import { Link } from 'react-router-dom';
import { Tag, Flex, Card } from 'antd';
import { MenuOutlined } from '@ant-design/icons';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { ETripTaskStatus } from 'types/trip-task-status';
import { TripTaskDeleteWarning } from 'components/trips/trip-tasks/components/trip-task-delete-warning';
import { TripTaskFleethandInfo } from 'components/trips/trip-tasks/components/trip-task-fleethand-info';
import type { ITripTaskItemProps } from 'components/trips/trip-tasks/components/trip-task-item/types';
import { TripTaskItemAddress } from 'components/trips/trip-tasks/components/trip-task-item-address/trip-task-item-address';
import { TripTaskItemCargoes } from 'components/trips/trip-tasks/components/trip-task-item-cargoes/trip-task-item-cargoes';
import { TripTaskItemComment } from 'components/trips/trip-tasks/components/trip-task-item-comment';
import { TripCargoItemControl } from 'components/trips/trip-tasks/components/trip-task-item-control/trip-task-item-control';
import { TripTaskItemStatus } from 'components/trips/trip-tasks/components/trip-task-item-status/trip-task-item-status';
import { TRIP_TASK_TYPE_MAP, TYPE_TAG_COLOR_MAP } from './constants';
import styles from './trip-task-item.module.scss';

export const TripTaskItem: React.FC<ITripTaskItemProps> = (props) => {
  const { trip, tripTask, mergeTasks = [], isDraggable = true } = props;
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id: tripTask.id,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform && { ...transform, scaleY: 1 }),
    transition,
    ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
  };

  const tripTaskTypeLabel = TRIP_TASK_TYPE_MAP[tripTask.type];
  const tripTaskLabelColor = TYPE_TAG_COLOR_MAP[tripTask.type];

  return (
    <Card ref={setNodeRef} className={styles.tripTaskItem} style={style} {...attributes}>
      <div className={styles.header}>
        <Flex gap='small'>
          {isDraggable && (
            <MenuOutlined ref={setActivatorNodeRef} style={{ touchAction: 'none', cursor: 'move' }} {...listeners} />
          )}
          <Tag color={tripTaskLabelColor} bordered={false}>
            #{tripTask.sequence} {tripTaskTypeLabel}
          </Tag>
          <TripTaskDeleteWarning fleethandSyncStatus={tripTask.fleethandSyncStatus} />
        </Flex>

        <Flex>
          <TripTaskItemComment comments={tripTask.comments} />
          <TripTaskItemStatus status={tripTask.status as ETripTaskStatus} />
          <TripCargoItemControl tripTask={tripTask} tripStatus={trip.status} mergeTasks={mergeTasks} />
        </Flex>
      </div>
      <div className={styles.content}>
        <TripTaskItemAddress address={tripTask.address} warehouseId={tripTask.warehouseId} />
        <TripTaskItemCargoes trip={trip} tripTaskId={tripTask.id} cargoTripIds={tripTask.cargoTripIds} />
        {tripTask.relatedTripId && (
          <div>
            Related trip: <Link to={`/trips/${tripTask.relatedTripId}`}>TKT-{tripTask.relatedTripId}</Link>
          </div>
        )}
        <TripTaskFleethandInfo tripTask={tripTask} />
      </div>
    </Card>
  );
};
